/**
 * T057/T118/T119: Prompt Detail Component
 * Full content display for a single prompt with copy and template generation
 */

import { FileText, Tag, Calendar, Edit, Trash } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { PromptContentViewer } from './prompt-content-viewer'
import { CopyButton } from './copy-button'
import { PromptGenerator } from './prompt-generator'
import type { PromptResponse, PromptContent } from '@/services/prompts'

interface PromptDetailProps {
  prompt: PromptResponse
  onDelete?: () => void
  onEdit?: () => void
}

function getContentAsText(content: PromptContent): string {
  if (content.type === 'text') {
    return content.text
  }
  if (content.type === 'chat') {
    return content.messages
      .map((m) => `[${m.role}]\n${m.content}`)
      .join('\n\n')
  }
  if (content.type === 'multipart') {
    return content.parts
      .filter((p) => p.type === 'text' && p.text)
      .map((p) => p.text)
      .join('\n\n')
  }
  return ''
}

export function PromptDetail({ prompt, onDelete, onEdit }: PromptDetailProps) {
  const formattedDate = new Date(prompt.updated_at).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div className="space-y-1">
          <h1 className="text-2xl font-bold">{prompt.title}</h1>
          <div className="flex flex-wrap items-center gap-3 text-sm text-muted-foreground">
            {prompt.category && (
              <div className="flex items-center gap-1">
                <FileText className="h-4 w-4" />
                <span>{prompt.category.name}</span>
              </div>
            )}
            <div className="flex items-center gap-1">
              <Calendar className="h-4 w-4" />
              <span>Updated {formattedDate}</span>
            </div>
            {prompt.is_template && (
              <Badge variant="secondary">Template</Badge>
            )}
          </div>
        </div>
        <div className="flex gap-2">
          {onEdit && (
            <Button variant="outline" size="sm" onClick={onEdit}>
              <Edit className="mr-2 h-4 w-4" />
              Edit
            </Button>
          )}
          {onDelete && (
            <Button variant="destructive" size="sm" onClick={onDelete}>
              <Trash className="mr-2 h-4 w-4" />
              Delete
            </Button>
          )}
        </div>
      </div>

      {prompt.tags && prompt.tags.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {prompt.tags.map((tag) => (
            <Badge key={tag.id} variant="outline">
              <Tag className="mr-1 h-3 w-3" />
              {tag.name}
            </Badge>
          ))}
        </div>
      )}

      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0">
          <CardTitle className="text-lg">Content</CardTitle>
          <CopyButton
            text={getContentAsText(prompt.content)}
            variant="outline"
            size="sm"
          >
            Copy Content
          </CopyButton>
        </CardHeader>
        <CardContent>
          <PromptContentViewer content={prompt.content} />
        </CardContent>
      </Card>

      {prompt.is_template && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Generate from Template</CardTitle>
          </CardHeader>
          <CardContent>
            <PromptGenerator promptId={prompt.id} />
          </CardContent>
        </Card>
      )}

      {prompt.config && Object.keys(prompt.config).length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Configuration</CardTitle>
          </CardHeader>
          <CardContent>
            <pre className="overflow-auto rounded-md bg-muted p-4 text-sm">
              {JSON.stringify(prompt.config, null, 2)}
            </pre>
          </CardContent>
        </Card>
      )}

      {prompt.metadata && Object.keys(prompt.metadata).length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Metadata</CardTitle>
          </CardHeader>
          <CardContent>
            <pre className="overflow-auto rounded-md bg-muted p-4 text-sm">
              {JSON.stringify(prompt.metadata, null, 2)}
            </pre>
          </CardContent>
        </Card>
      )}
    </div>
  )
}
